import { HttpClient } from '@angular/common/http';
import { computed, Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { switchMap, tap } from 'rxjs';

import { Language, LanguageStore } from './language.store';

type TranslationData = Record<string, unknown>;

@Injectable({
  providedIn: 'root',
})
export class TranslateService {
  private readonly http = inject(HttpClient);
  private readonly languageStore = inject(LanguageStore);

  private readonly _translations = signal<TranslationData>({});
  private readonly _loaded = signal(false);

  readonly translations = this._translations.asReadonly();
  readonly loaded = this._loaded.asReadonly();
  readonly currentLanguage = this.languageStore.language;

  constructor() {
    toObservable(this.languageStore.language)
      .pipe(
        switchMap((lang) => this.loadTranslations(lang)),
        tap((data) => {
          this._translations.set(data);
          this._loaded.set(true);
        }),
      )
      .subscribe();
  }

  private loadTranslations(lang: Language) {
    return this.http.get<TranslationData>(`/assets/i18n/${lang}.json`);
  }

  get(key: string, params?: Record<string, string | number>): string {
    const keys = key.split('.');
    let value: unknown = this._translations();

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        return key;
      }
    }

    if (typeof value !== 'string') {
      return key;
    }

    if (params) {
      return Object.entries(params).reduce(
        (str, [paramKey, paramValue]) =>
          str.replace(new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g'), String(paramValue)),
        value,
      );
    }

    return value;
  }

  t(key: string, params?: Record<string, string | number>) {
    return computed(() => {
      this._translations();
      return this.get(key, params);
    });
  }
}
